import { VideoEditor } from '../VideoEditor';
import { Element } from '../models/Element';
import { VideoEditorLogger } from '../../utility/VideoEditorLogger';
import { TimelineMode } from './TimelineMode';
import { AbstractLayoutCommand } from './AbstractLayoutCommand';

export interface ExciseLayoutElementsParameters {
  mediaRemovalStart: number;
  mediaRemovalEnd: number;
}

export interface ResultElements {
  parent: Element | null;
  top: Element | null;
  bottom: Element | null;
}

export class ExciseLayoutElementsCommand extends AbstractLayoutCommand {
  private params: ExciseLayoutElementsParameters;
  private _firstPartElements: ResultElements | null = null;
  private _secondPartElements: ResultElements | null = null;

  constructor(
    editor: VideoEditor,
    element: Element,
    params: ExciseLayoutElementsParameters,
  ) {
    super(editor, element);
    this.params = params;
  }

  execute(): boolean {
    if (!this.sourceElement) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'No source element provided',
      });
      return false;
    }

    VideoEditorLogger.logCommand(this, 'start', {
      elementId: this.sourceElement?.id,
      params: this.params,
      extraElementData: this.extraElementData,
    });

    super.execute(); // makes a snapshot

    const elements = this.pickAllLayoutElements(this.sourceElement);
    if (!elements) {
      VideoEditorLogger.logCommand(this, 'error', {
        error:
          'Failed to remove parent: no parent found or not a layout element',
      });
      return false;
    }
    const {
      parent: parentElement,
      children: childrenElements,
      data: layoutElementsData,
    } = elements;

    // Get top and bottom elements using the helper method
    const layoutElements = this.separateChildrenByPosition(childrenElements);
    if (!layoutElements) return false;
    const { top: topElement, bottom: bottomElement } = layoutElements;

    const trackManager = this.editor.getTrackManager();

    // Excise time range from parent, top, and bottom elements
    const parentExciseResult = trackManager.exciseTimeRangeFromElement(
      parentElement,
      this.params.mediaRemovalStart,
      this.params.mediaRemovalEnd,
      TimelineMode.RIPPLE,
    );
    if (!parentExciseResult) return false;

    const topExciseResult = trackManager.exciseTimeRangeFromElement(
      topElement,
      this.params.mediaRemovalStart,
      this.params.mediaRemovalEnd,
    );
    if (!topExciseResult) return false;

    const bottomExciseResult = trackManager.exciseTimeRangeFromElement(
      bottomElement,
      this.params.mediaRemovalStart,
      this.params.mediaRemovalEnd,
    );
    if (!bottomExciseResult) return false;

    // Store the resulting elements for undo/redo
    this._firstPartElements = {
      parent: parentExciseResult.firstPart,
      top: topExciseResult.firstPart,
      bottom: bottomExciseResult.firstPart,
    };
    this._secondPartElements = {
      parent: parentExciseResult.secondPart,
      top: topExciseResult.secondPart,
      bottom: bottomExciseResult.secondPart,
    };
    this.canUndo = true;

    // Update extraElementData to glue the new elements into stacked layouts
    if (
      this._firstPartElements.parent &&
      this._firstPartElements.top &&
      this._firstPartElements.bottom
    ) {
      this.extraElementData.setLayoutElements(
        this._firstPartElements.parent,
        this._firstPartElements.top,
        this._firstPartElements.bottom,
        layoutElementsData.layoutType ?? 'stacked-1-1',
      );
    }
    if (
      this._secondPartElements.parent &&
      this._secondPartElements.top &&
      this._secondPartElements.bottom
    ) {
      this.extraElementData.setLayoutElements(
        this._secondPartElements.parent,
        this._secondPartElements.top,
        this._secondPartElements.bottom,
        layoutElementsData.layoutType ?? 'stacked-1-1',
      );
    }

    this.recalculateTimeline();

    return true;
  }

  getDescription(): string {
    return `Excise time range from layout elements (${this.sourceElement?.id})`;
  }

  getResultElements(): [ResultElements, ResultElements] | null {
    if (!this._firstPartElements || !this._secondPartElements) {
      return null;
    }
    return [this._firstPartElements, this._secondPartElements];
  }
}
